import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { patientsAPI, usersAPI, assignmentsAPI, centersAPI, reportsAPI, kpiAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import AccessibleModal from '../components/common/AccessibleModal';
import { CANADIAN_PROVINCES_AND_TERRITORIES } from '../constants/canada';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const toDateTimeLocal = (value = new Date()) => {
  const d = value instanceof Date ? value : new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('fr-CA');
};

// ===== MODAL FORMULAIRE COMPLET DE PRISE EN CHARGE =====
const PatientIntakeModal = ({ patient, onClose, onSave, t, isReadOnly }) => {
  const { user } = useAuth();
  const canManageAssignments = user?.role === 'admin' || user?.role === 'manager';
  const canAssignDoctor = user?.role === 'admin' || user?.role === 'manager';
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const nowDateTimeLocal = toDateTimeLocal(now);
  const monthStartDateTimeLocal = toDateTimeLocal(monthStart);
  const todayIso = now.toISOString().slice(0, 10);
  const [form, setForm] = useState({
    // Adresse usager
    streetNumber: patient?.street_number || '',
    streetName: patient?.street_name || '',
    apartment: patient?.apartment || '',
    city: patient?.city || '',
    province: patient?.province || '',
    postalCode: patient?.postal_code || '',

    // Informations de base
    dateOfBirth: patient?.date_of_birth?.split('T')[0] || '',
    gender: patient?.gender || '',
    healthCardNumber: patient?.health_card_number || '',
    primaryLanguage: patient?.primary_language || 'Français',
    interpreterNeeded: patient?.interpreter_needed || false,
    
    // Contact d'urgence
    emergencyContactName: patient?.emergency_contact_name || '',
    emergencyContactPhone: patient?.emergency_contact_phone || '',
    emergencyContactRelation: patient?.emergency_contact_relation || '',
    
    // Contact famille
    familyContactName: patient?.family_contact_name || '',
    familyContactPhone: patient?.family_contact_phone || '',
    familyContactRelation: patient?.family_contact_relation || '',
    
    // Médecin traitant
    doctorName: patient?.doctor_name || '',
    doctorPhone: patient?.doctor_phone || '',
    
    // Santé
    bloodType: patient?.blood_type || '',
    allergies: patient?.allergies || '',
    currentMedications: patient?.current_medications || '',
    medicalConditions: patient?.medical_conditions || '',
    
    // Assurance
    insuranceProvider: patient?.insurance_provider || '',
    insuranceNumber: patient?.insurance_number || '',
    
    // Évaluation fonctionnelle
    mobilityStatus: patient?.mobility_status || '',
    cognitiveStatus: patient?.cognitive_status || '',
    
    // Notes cliniques
    behavioralNotes: patient?.behavioral_notes || '',
    nutritionNotes: patient?.nutrition_notes || '',
    communicationNotes: patient?.communication_notes || '',
    
    // Plan de soins
    carePlan: patient?.care_plan || '',
    goals: patient?.goals || '',
    servicesRequired: patient?.services_required || '',
    
    // Logistique
    schedulePreferences: patient?.schedule_preferences || '',
    transportationNeeds: patient?.transportation_needs || '',
    financialInfo: patient?.financial_info || '',
    
    // Admission
    admissionDate: patient?.admission_date?.split('T')[0] || '',
    referredBy: patient?.referred_by || '',
    referralDate: patient?.referral_date?.split('T')[0] || '',
    
    // Consentement
    consentSigned: patient?.consent_signed || false,
    consentDate: patient?.consent_date?.split('T')[0] || '',
    intakeCompletedBy: patient?.intake_completed_by || '',
    intakeDate: patient?.intake_date?.split('T')[0] || '',
    
    // Autres
    notes: patient?.notes || '',
  });
  
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    staffId: '',
    assignmentType: 'primary',
    startsAt: '',
    notes: '',
  });
  const [kpiStartDate, setKpiStartDate] = useState(monthStartDateTimeLocal);
  const [kpiEndDate, setKpiEndDate] = useState(nowDateTimeLocal);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [patientKpi, setPatientKpi] = useState(null);
  const [kpiMessage, setKpiMessage] = useState('');
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [savingHospitalization, setSavingHospitalization] = useState(false);
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [completingFollowupId, setCompletingFollowupId] = useState(null);
  const [loadingFollowups, setLoadingFollowups] = useState(false);
  const [careFollowups, setCareFollowups] = useState([]);
  const [assessmentForm, setAssessmentForm] = useState({
    metricType: 'socialization',
    score: '3',
    notes: '',
    assessedAt: nowDateTimeLocal,
  });
  const [surveyForm, setSurveyForm] = useState({
    surveyType: 'family_caregiver',
    rating: '4',
    comment: '',
    submittedAt: nowDateTimeLocal,
  });
  const [hospitalizationForm, setHospitalizationForm] = useState({
    admissionDate: nowDateTimeLocal,
    dischargeDate: '',
    reason: '',
  });
  const [followupForm, setFollowupForm] = useState({
    dueDate: nowDateTimeLocal,
    notes: '',
  });
  const patientKpiChartData = patientKpi ? [
    { label: 'Socialisation', value: Number(patientKpi.socialization_rate_percent || 0) },
    { label: 'Autonomie', value: Number(patientKpi.autonomy_progress_rate_percent || 0) },
    { label: 'Famille', value: Number(patientKpi.family_caregiver_satisfaction_rate_percent || 0) },
    { label: 'Partic. activités', value: Number(patientKpi.user_activity_participation_rate_percent || 0) },
    { label: 'Suivi soins', value: Number(patientKpi.care_followup_rate_percent || 0) },
    { label: 'Repas', value: Number(patientKpi.meal_satisfaction_rate_percent || 0) },
    { label: 'Présence', value: Number(patientKpi.presence_rate_percent || 0) },
  ] : [];

  const loadCareFollowups = async () => {
    if (!patient?.id) return;
    setLoadingFollowups(true);
    try {
      const res = await kpiAPI.getCareFollowups({
        patientId: patient.id,
        status: 'pending',
      });
      setCareFollowups(res.data || []);
    } catch (err) {
      console.error(err);
      setCareFollowups([]);
    } finally {
      setLoadingFollowups(false);
    }
  };

  const loadAssignments = async () => {
    if (!patient?.id) return;
    setLoadingAssignments(true);
    try {
      const [activeRes, allRes] = await Promise.all([
        assignmentsAPI.getPatientAssignments(patient.id, { active: 'true' }),
        assignmentsAPI.getPatientAssignments(patient.id, { active: 'false' }),
      ]);
      setAssignments(activeRes.data || []);
      setAssignmentHistory(allRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadStaffOptions = async () => {
    if (!canManageAssignments) return;
    try {
      const res = await usersAPI.getAll({ role: 'staff', limit: 500 });
      const users = res.data?.users || [];
      const pabOnly = users.filter((u) => {
        const fn = String(u.job_function || '').toLowerCase();
        return fn === 'pab' || fn.includes('prepose') || fn.includes('préposé');
      });
      setStaffOptions(pabOnly);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDoctorOptions = async () => {
    if (!canAssignDoctor) return;
    try {
      const res = await usersAPI.getAll({ role: 'staff', limit: 500 });
      const users = res.data?.users || [];
      const doctors = users.filter((u) => {
        const fn = String(u.job_function || '').toLowerCase();
        return fn.includes('medecin') || fn.includes('médecin') || fn.includes('doctor');
      });
      setDoctorOptions(doctors);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadAssignments();
    loadStaffOptions();
    loadDoctorOptions();
  }, [patient?.id]);

  const loadPatientKpis = async () => {
    if (!patient?.id) return;
    setKpiLoading(true);
    try {
      const res = await reportsAPI.getPatientKpis({
        patient_id: patient.id,
        start: kpiStartDate,
        end: kpiEndDate,
      });
      const item = res.data?.items?.[0] || null;
      setPatientKpi(item);
    } catch (err) {
      console.error(err);
      setPatientKpi(null);
    } finally {
      setKpiLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'kpis') return;
    loadPatientKpis();
    loadCareFollowups();
  }, [activeTab, patient?.id, kpiStartDate, kpiEndDate]);

  const showKpiSuccess = (msg) => {
    setKpiMessage(msg);
    setTimeout(() => setKpiMessage(''), 3500);
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    if (!patient?.id) return;
    setSavingAssessment(true);
    try {
      await kpiAPI.createAssessment({
        patientId: patient.id,
        metricType: assessmentForm.metricType,
        score: Number(assessmentForm.score),
        notes: assessmentForm.notes || null,
        assessedAt: assessmentForm.assessedAt || null,
      });
      setAssessmentForm((prev) => ({ ...prev, notes: '' }));
      showKpiSuccess('Évaluation KPI enregistrée.');
      await loadPatientKpis();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la saisie de l’évaluation KPI');
    } finally {
      setSavingAssessment(false);
    }
  };

  const handleCreateSurvey = async (e) => {
    e.preventDefault();
    if (user?.role !== 'usager') {
      alert('Sondage en lecture seule pour ce profil');
      return;
    }
    if (!patient?.id) return;
    setSavingSurvey(true);
    try {
      await kpiAPI.createSurvey({
        surveyType: surveyForm.surveyType,
        patientId: patient.id,
        rating: Number(surveyForm.rating),
        comment: surveyForm.comment || null,
        submittedAt: surveyForm.submittedAt || null,
      });
      setSurveyForm((prev) => ({ ...prev, comment: '' }));
      showKpiSuccess('Sondage KPI enregistré.');
      await loadPatientKpis();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la saisie du sondage KPI');
    } finally {
      setSavingSurvey(false);
    }
  };

  const handleCreateHospitalization = async (e) => {
    e.preventDefault();
    if (!patient?.id || !hospitalizationForm.admissionDate) return;
    setSavingHospitalization(true);
    try {
      await kpiAPI.createHospitalization({
        patientId: patient.id,
        admissionDate: hospitalizationForm.admissionDate,
        dischargeDate: hospitalizationForm.dischargeDate || null,
        reason: hospitalizationForm.reason || null,
      });
      setHospitalizationForm({ admissionDate: nowDateTimeLocal, dischargeDate: '', reason: '' });
      showKpiSuccess('Hospitalisation enregistrée.');
      await loadPatientKpis();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la saisie de l’hospitalisation');
    } finally {
      setSavingHospitalization(false);
    }
  };

  const handleCreateCareFollowup = async (e) => {
    e.preventDefault();
    if (!patient?.id || !followupForm.dueDate) return;
    setSavingFollowup(true);
    try {
      await kpiAPI.createCareFollowup({
        patientId: patient.id,
        dueDate: followupForm.dueDate,
        notes: followupForm.notes || null,
      });
      setFollowupForm({ dueDate: nowDateTimeLocal, notes: '' });
      showKpiSuccess('Suivi de soin créé.');
      await loadPatientKpis();
      await loadCareFollowups();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la création du suivi de soin');
    } finally {
      setSavingFollowup(false);
    }
  };

  const handleCompleteCareFollowup = async (followupId) => {
    if (!followupId) return;
    setCompletingFollowupId(followupId);
    try {
      await kpiAPI.completeCareFollowup(followupId);
      showKpiSuccess('Suivi de soin marqué complété.');
      await loadPatientKpis();
      await loadCareFollowups();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la clôture du suivi de soin');
    } finally {
      setCompletingFollowupId(null);
    }
  };

  const handleAssignDoctor = () => {
    if (!selectedDoctorId) return;
    const doctor = doctorOptions.find(d => d.id === selectedDoctorId);
    if (!doctor) return;
    setForm(f => ({
      ...f,
      doctorName: `${doctor.first_name} ${doctor.last_name}`.trim(),
      doctorPhone: doctor.phone || f.doctorPhone,
    }));
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!assignmentForm.staffId) return;
    try {
      await assignmentsAPI.createPatientAssignment(patient.id, {
        staffId: assignmentForm.staffId,
        assignmentType: assignmentForm.assignmentType,
        startsAt: assignmentForm.startsAt || null,
        notes: assignmentForm.notes || null,
      });
      setAssignmentForm({ staffId: '', assignmentType: 'primary', startsAt: '', notes: '' });
      await loadAssignments();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la création de l’assignation');
    }
  };

  const handleEndAssignment = async (assignmentId) => {
    if (!window.confirm('Terminer cette assignation ?')) return;
    try {
      await assignmentsAPI.endPatientAssignment(patient.id, assignmentId);
      await loadAssignments();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la fin d’assignation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await patientsAPI.updateRecord(patient.id, form);
      onSave();
    } catch (err) {
      console.error(err);
      const validationMsg = err?.response?.data?.errors?.[0]?.msg;
      alert(validationMsg || err?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: '📋 Informations', icon: '📋' },
    { id: 'health', label: '🏥 Santé', icon: '🏥' },
    { id: 'evaluation', label: '🧠 Évaluation', icon: '🧠' },
    { id: 'care', label: '💊 Plan de soins', icon: '💊' },
    { id: 'logistics', label: '🚗 Logistique', icon: '🚗' },
    { id: 'admission', label: '📝 Admission', icon: '📝' },
    { id: 'assignments', label: '👩‍⚕️ PAB', icon: '👩‍⚕️' },
    { id: 'kpis', label: '📊 KPI', icon: '📊' },
  ];

  return (
    <AccessibleModal
      title={(
        <div>
          <span>🏥 Dossier de prise en charge</span>
          <p style={{ fontSize: 14, color: 'var(--gray-500)', marginTop: 4 }}>
            {patient?.first_name} {patient?.last_name}
          </p>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 4, marginBottom: 0 }}>
            PIN tablette: {patient?.has_timeclock_pin ? 'Configuré' : 'Absent'}
          </p>
        </div>
      )}
      onClose={onClose}
      maxWidth={1200}
      footer={!isReadOnly ? (
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Fermer</button>
          <button type="submit" form="patient-intake-form" className="btn btn-primary" disabled={loading}>
            {loading ? 'Enregistrement...' : '💾 Enregistrer'}
          </button>
        </>
      ) : null}
    >
        {/* Tabs */}
        <div style={{ 
          borderBottom: '1px solid var(--gray-200)', 
          padding: '0 24px', 
          display: 'flex', 
          gap: 8,
          overflowX: 'auto',
          flexWrap: 'wrap',
          rowGap: 6
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: activeTab === tab.id ? 'var(--primary)' : 'var(--gray-500)',
                borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.icon} {tab.label.split(' ')[1]}
            </button>
          ))}
        </div>

        <form id="patient-intake-form" onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            
            {/* TAB 1: INFORMATIONS DE BASE */}
            {activeTab === 'basic' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  📋 Informations personnelles
                </h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Date de naissance</label>
                    <input type="date" className="form-input" value={form.dateOfBirth} 
                      onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Genre</label>
                    <select className="form-select" value={form.gender} 
                      onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} disabled={isReadOnly}>
                      <option value="">—</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                      <option value="NB">Non-binaire</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Carte santé (RAMQ)</label>
                    <input className="form-input" value={form.healthCardNumber} 
                      onChange={e => setForm(f => ({ ...f, healthCardNumber: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Langue principale</label>
                    <select className="form-select" value={form.primaryLanguage} 
                      onChange={e => setForm(f => ({ ...f, primaryLanguage: e.target.value }))} disabled={isReadOnly}>
                      <option value="Français">Français</option>
                      <option value="English">Anglais</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.interpreterNeeded} 
                      onChange={e => setForm(f => ({ ...f, interpreterNeeded: e.target.checked }))} disabled={isReadOnly} />
                    <span>Interprète requis</span>
                  </label>
                </div>

                <hr style={{ margin: '24px 0' }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>🏠 Adresse de l'usager</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Num civique</label>
                    <input
                      className="form-input"
                      value={form.streetNumber}
                      onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nom de la rue</label>
                    <input
                      className="form-input"
                      value={form.streetName}
                      onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Suite/App.</label>
                    <input
                      className="form-input"
                      value={form.apartment}
                      onChange={e => setForm(f => ({ ...f, apartment: e.target.value }))}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ville</label>
                    <input
                      className="form-input"
                      value={form.city}
                      onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Province</label>
                    <select
                      className="form-select"
                      value={form.province}
                      onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                      disabled={isReadOnly}
                    >
                      <option value="">— Sélectionner —</option>
                      {CANADIAN_PROVINCES_AND_TERRITORIES.map((province) => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Code postal</label>
                    <input
                      className="form-input"
                      value={form.postalCode}
                      onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <hr style={{ margin: '24px 0' }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>👥 Contact d'urgence</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nom complet</label>
                    <input className="form-input" value={form.emergencyContactName} 
                      onChange={e => setForm(f => ({ ...f, emergencyContactName: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Téléphone</label>
                    <input type="tel" className="form-input" value={form.emergencyContactPhone} 
                      onChange={e => setForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} disabled={isReadOnly} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Lien avec le patient</label>
                  <input className="form-input" value={form.emergencyContactRelation} 
                    onChange={e => setForm(f => ({ ...f, emergencyContactRelation: e.target.value }))} 
                    placeholder="Ex: Conjoint(e), Fils/Fille" disabled={isReadOnly} />
                </div>

                <hr style={{ margin: '24px 0' }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>👨‍👩‍👧 Proche aidant / Famille</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nom</label>
                    <input className="form-input" value={form.familyContactName} 
                      onChange={e => setForm(f => ({ ...f, familyContactName: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Téléphone</label>
                    <input type="tel" className="form-input" value={form.familyContactPhone} 
                      onChange={e => setForm(f => ({ ...f, familyContactPhone: e.target.value }))} disabled={isReadOnly} />
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: SANTÉ & MÉDICAL */}
            {activeTab === 'health' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  🏥 Informations médicales
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Médecin traitant (nom complet)</label>
                    <input
                      className="form-input"
                      value={form.doctorName}
                      placeholder="Dr. Nom Prénom"
                      disabled
                      style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Assigner un médecin</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select
                        className="form-select"
                        value={selectedDoctorId}
                        onChange={e => setSelectedDoctorId(e.target.value)}
                        disabled={isReadOnly || !canAssignDoctor || doctorOptions.length === 0}
                      >
                        <option value="">{doctorOptions.length === 0 ? 'Aucun médecin disponible' : 'Sélectionner un médecin'}</option>
                        {doctorOptions.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.first_name} {d.last_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleAssignDoctor}
                        disabled={isReadOnly || !canAssignDoctor || doctorOptions.length === 0 || !selectedDoctorId}
                      >
                        Assigner
                      </button>
                    </div>
                    {!isReadOnly && canAssignDoctor && doctorOptions.length === 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-500)' }}>
                        Aucun utilisateur avec la fonction « Médecin » n'est disponible.
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Téléphone du médecin</label>
                    <input type="tel" className="form-input" value={form.doctorPhone} 
                      onChange={e => setForm(f => ({ ...f, doctorPhone: e.target.value }))} disabled={isReadOnly} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Groupe sanguin</label>
                    <select className="form-select" value={form.bloodType} 
                      onChange={e => setForm(f => ({ ...f, bloodType: e.target.value }))} disabled={isReadOnly}>
                      <option value="">—</option>
                      {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Assurance</label>
                    <input className="form-input" value={form.insuranceProvider} 
                      onChange={e => setForm(f => ({ ...f, insuranceProvider: e.target.value }))} disabled={isReadOnly} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">No. d'assurance</label>
                  <input className="form-input" value={form.insuranceNumber} 
                    onChange={e => setForm(f => ({ ...f, insuranceNumber: e.target.value }))} disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Allergies</label>
                  <textarea className="form-textarea" value={form.allergies} 
                    onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))} rows={3} disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Médicaments actuels</label>
                  <textarea className="form-textarea" value={form.currentMedications} 
                    onChange={e => setForm(f => ({ ...f, currentMedications: e.target.value }))} rows={3} disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Conditions médicales / Diagnostics</label>
                  <textarea className="form-textarea" value={form.medicalConditions} 
                    onChange={e => setForm(f => ({ ...f, medicalConditions: e.target.value }))} rows={3} disabled={isReadOnly} />
                </div>
              </div>
            )}

            {/* TAB 3: ÉVALUATION FONCTIONNELLE */}
            {activeTab === 'evaluation' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  🧠 Évaluation fonctionnelle
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">État de mobilité</label>
                    <select className="form-select" value={form.mobilityStatus} 
                      onChange={e => setForm(f => ({ ...f, mobilityStatus: e.target.value }))} disabled={isReadOnly}>
                      <option value="">—</option>
                      <option value="Autonome">Autonome</option>
                      <option value="Canne">Avec canne</option>
                      <option value="Marchette">Avec marchette</option>
                      <option value="Fauteuil roulant">Fauteuil roulant</option>
                      <option value="Aide partielle">Aide partielle requise</option>
                      <option value="Aide complète">Aide complète requise</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">État cognitif</label>
                    <select className="form-select" value={form.cognitiveStatus} 
                      onChange={e => setForm(f => ({ ...f, cognitiveStatus: e.target.value }))} disabled={isReadOnly}>
                      <option value="">—</option>
                      <option value="Alerte et orienté">Alerte et orienté</option>
                      <option value="Confusion légère">Confusion légère</option>
                      <option value="Confusion modérée">Confusion modérée</option>
                      <option value="Démence légère">Démence légère</option>
                      <option value="Démence modérée">Démence modérée</option>
                      <option value="Démence avancée">Démence avancée</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes comportementales</label>
                  <textarea className="form-textarea" value={form.behavioralNotes} 
                    onChange={e => setForm(f => ({ ...f, behavioralNotes: e.target.value }))} 
                    rows={3} placeholder="Comportements, habitudes, préférences sociales..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes nutritionnelles</label>
                  <textarea className="form-textarea" value={form.nutritionNotes} 
                    onChange={e => setForm(f => ({ ...f, nutritionNotes: e.target.value }))} 
                    rows={2} placeholder="Régime, allergies alimentaires, préférences..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Notes de communication</label>
                  <textarea className="form-textarea" value={form.communicationNotes} 
                    onChange={e => setForm(f => ({ ...f, communicationNotes: e.target.value }))} 
                    rows={2} placeholder="Déficiences auditives, visuelles, préférences..." disabled={isReadOnly} />
                </div>
              </div>
            )}

            {/* TAB 4: PLAN DE SOINS */}
            {activeTab === 'care' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  💊 Plan de soins et objectifs
                </h4>

                <div className="form-group">
                  <label className="form-label">Plan de soins</label>
                  <textarea className="form-textarea" value={form.carePlan} 
                    onChange={e => setForm(f => ({ ...f, carePlan: e.target.value }))} 
                    rows={4} placeholder="Interventions, soins requis, fréquence..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Objectifs de soins</label>
                  <textarea className="form-textarea" value={form.goals} 
                    onChange={e => setForm(f => ({ ...f, goals: e.target.value }))} 
                    rows={3} placeholder="Objectifs à court et long terme..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Services requis</label>
                  <textarea className="form-textarea" value={form.servicesRequired} 
                    onChange={e => setForm(f => ({ ...f, servicesRequired: e.target.value }))} 
                    rows={3} placeholder="Ergothérapie, physiothérapie, soins infirmiers..." disabled={isReadOnly} />
                </div>
              </div>
            )}

            {/* TAB 5: LOGISTIQUE */}
            {activeTab === 'logistics' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  🚗 Logistique et préférences
                </h4>

                <div className="form-group">
                  <label className="form-label">Préférences d'horaire</label>
                  <textarea className="form-textarea" value={form.schedulePreferences} 
                    onChange={e => setForm(f => ({ ...f, schedulePreferences: e.target.value }))} 
                    rows={2} placeholder="Jours préférés, heures préférées..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Besoins de transport</label>
                  <textarea className="form-textarea" value={form.transportationNeeds} 
                    onChange={e => setForm(f => ({ ...f, transportationNeeds: e.target.value }))} 
                    rows={2} placeholder="Transport adapté requis, accompagnement..." disabled={isReadOnly} />
                </div>

                <div className="form-group">
                  <label className="form-label">Informations financières</label>
                  <textarea className="form-textarea" value={form.financialInfo} 
                    onChange={e => setForm(f => ({ ...f, financialInfo: e.target.value }))} 
                    rows={2} placeholder="Assurances, aide financière, modalités de paiement..." disabled={isReadOnly} />
                </div>
              </div>
            )}

            {/* TAB 6: ADMISSION & CONSENTEMENT */}
            {activeTab === 'admission' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  📝 Admission et consentements
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Date d'admission</label>
                    <input type="date" className="form-input" value={form.admissionDate} 
                      onChange={e => setForm(f => ({ ...f, admissionDate: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Référé par</label>
                    <input className="form-input" value={form.referredBy} 
                      onChange={e => setForm(f => ({ ...f, referredBy: e.target.value }))} 
                      placeholder="Nom, organisme" disabled={isReadOnly} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Date de référence</label>
                    <input type="date" className="form-input" value={form.referralDate} 
                      onChange={e => setForm(f => ({ ...f, referralDate: e.target.value }))} disabled={isReadOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date de prise en charge</label>
                    <input type="date" className="form-input" value={form.intakeDate} 
                      onChange={e => setForm(f => ({ ...f, intakeDate: e.target.value }))} disabled={isReadOnly} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Complété par (intervenant)</label>
                  <input className="form-input" value={form.intakeCompletedBy} 
                    onChange={e => setForm(f => ({ ...f, intakeCompletedBy: e.target.value }))} 
                    placeholder="Nom de l'intervenant" disabled={isReadOnly} />
                </div>

                <hr style={{ margin: '24px 0' }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>✍️ Consentement</h4>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.consentSigned} 
                      onChange={e => setForm(f => ({ ...f, consentSigned: e.target.checked }))} disabled={isReadOnly} />
                    <span>Consentement signé</span>
                  </label>
                </div>

                {form.consentSigned && (
                  <div className="form-group">
                    <label className="form-label">Date du consentement</label>
                    <input type="date" className="form-input" value={form.consentDate} 
                      onChange={e => setForm(f => ({ ...f, consentDate: e.target.value }))} disabled={isReadOnly} />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notes additionnelles</label>
                  <textarea className="form-textarea" value={form.notes} 
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} 
                    rows={3} placeholder="Autres informations pertinentes..." disabled={isReadOnly} />
                </div>
              </div>
            )}

            {/* TAB 7: ASSIGNATION PAB */}
            {activeTab === 'assignments' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  👩‍⚕️ Assignation PAB
                </h4>

                {canManageAssignments && !isReadOnly && (
                  <div style={{
                    border: '1px solid var(--gray-200)',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 20,
                    background: 'var(--gray-50)',
                  }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">PAB *</label>
                        <select
                          className="form-select"
                          value={assignmentForm.staffId}
                          onChange={e => setAssignmentForm(f => ({ ...f, staffId: e.target.value }))}
                          required
                          disabled={staffOptions.length === 0}
                        >
                          <option value="">{staffOptions.length === 0 ? 'Aucun PAB disponible' : 'Sélectionner un PAB'}</option>
                          {staffOptions.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.first_name} {s.last_name} ({s.employee_number || 'N/A'})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Type</label>
                        <select
                          className="form-select"
                          value={assignmentForm.assignmentType}
                          onChange={e => setAssignmentForm(f => ({ ...f, assignmentType: e.target.value }))}
                        >
                          <option value="primary">Principal</option>
                          <option value="secondary">Secondaire</option>
                          <option value="temporary">Temporaire</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Date de début</label>
                        <input
                          type="date"
                          className="form-input"
                          value={assignmentForm.startsAt}
                          onChange={e => setAssignmentForm(f => ({ ...f, startsAt: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <input
                          className="form-input"
                          value={assignmentForm.notes}
                          onChange={e => setAssignmentForm(f => ({ ...f, notes: e.target.value }))}
                          placeholder="Contexte d'assignation"
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCreateAssignment}
                        disabled={staffOptions.length === 0 || !assignmentForm.staffId}
                      >
                        + Assigner
                      </button>
                    </div>
                    {staffOptions.length === 0 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray-500)' }}>
                        Aucun PAB disponible pour ce centre pour le moment.
                      </div>
                    )}
                  </div>
                )}

                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Assignations actives</h4>
                {loadingAssignments ? (
                  <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
                ) : assignments.length === 0 ? (
                  <div style={{ padding: 12, color: 'var(--gray-500)' }}>Aucune assignation active.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {assignments.map(a => (
                      <div key={a.id} style={{
                        border: '1px solid var(--gray-200)',
                        borderRadius: 10,
                        padding: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {a.staff_first_name} {a.staff_last_name}
                            <span className="badge badge-primary" style={{ marginLeft: 8 }}>{a.assignment_type}</span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            Début: {a.starts_at ? String(a.starts_at).split('T')[0] : '—'}
                            {a.notes ? ` • ${a.notes}` : ''}
                          </div>
                        </div>
                        {canManageAssignments && (
                          <button className="btn btn-sm btn-outline" onClick={() => handleEndAssignment(a.id)}>
                            Terminer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <hr style={{ margin: '20px 0' }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Historique</h4>
                {assignmentHistory.length === 0 ? (
                  <div style={{ padding: 12, color: 'var(--gray-500)' }}>Aucun historique.</div>
                ) : (
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
                    {assignmentHistory.map(a => (
                      <div key={a.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                        <strong>{a.staff_first_name} {a.staff_last_name}</strong> ({a.assignment_type})
                        {' '}• {a.is_active ? 'Actif' : 'Terminé'}
                        {' '}• {a.starts_at ? String(a.starts_at).split('T')[0] : '—'}
                        {a.ends_at ? ` → ${String(a.ends_at).split('T')[0]}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB 8: KPI USAGER */}
            {activeTab === 'kpis' && (
              <div>
                <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--primary)' }}>
                  📊 KPI usager
                </h4>

                {!isReadOnly && (
                  <>
                    {kpiMessage && (
                      <div className="alert alert-success" style={{ marginBottom: 16 }}>
                        ✅ {kpiMessage}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                      <form onSubmit={handleCreateAssessment} style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 12 }}>
                        <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Évaluation (socialisation/autonomie)</h5>
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Type</label>
                            <select
                              className="form-select"
                              value={assessmentForm.metricType}
                              onChange={e => setAssessmentForm(f => ({ ...f, metricType: e.target.value }))}
                            >
                              <option value="socialization">Socialisation</option>
                              <option value="autonomy">Autonomie</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Score (1-5)</label>
                            <input
                              type="number"
                              min="1"
                              max="5"
                              step="1"
                              className="form-input"
                              value={assessmentForm.score}
                              onChange={e => setAssessmentForm(f => ({ ...f, score: e.target.value }))}
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Date</label>
                          <input
                            type="datetime-local"
                            className="form-input"
                            value={assessmentForm.assessedAt}
                            onChange={e => setAssessmentForm(f => ({ ...f, assessedAt: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-textarea"
                            rows={2}
                            value={assessmentForm.notes}
                            onChange={e => setAssessmentForm(f => ({ ...f, notes: e.target.value }))}
                          />
                        </div>
                        <button className="btn btn-primary btn-sm" type="submit" disabled={savingAssessment}>
                          {savingAssessment ? 'Enregistrement...' : 'Ajouter'}
                        </button>
                      </form>

                      <form onSubmit={handleCreateSurvey} style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 12 }}>
                        <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Sondage (proche aidant/repas)</h5>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>
                          Lecture seule (modifiable uniquement par l&apos;usager).
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Type</label>
                            <select
                              className="form-select"
                              value={surveyForm.surveyType}
                              onChange={e => setSurveyForm(f => ({ ...f, surveyType: e.target.value }))}
                              disabled
                            >
                              <option value="family_caregiver">Proche aidant</option>
                              <option value="meal">Repas</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Note (1-5)</label>
                            <input
                              type="number"
                              min="1"
                              max="5"
                              step="1"
                              className="form-input"
                              value={surveyForm.rating}
                              onChange={e => setSurveyForm(f => ({ ...f, rating: e.target.value }))}
                              disabled
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Date</label>
                          <input
                            type="datetime-local"
                            className="form-input"
                            value={surveyForm.submittedAt}
                            onChange={e => setSurveyForm(f => ({ ...f, submittedAt: e.target.value }))}
                            disabled
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Commentaire</label>
                          <textarea
                            className="form-textarea"
                            rows={2}
                            value={surveyForm.comment}
                            onChange={e => setSurveyForm(f => ({ ...f, comment: e.target.value }))}
                            disabled
                          />
                        </div>
                        <button className="btn btn-primary btn-sm" type="submit" disabled>
                          Lecture seule
                        </button>
                      </form>

                      <form onSubmit={handleCreateHospitalization} style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 12 }}>
                        <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Hospitalisation</h5>
                        <div className="form-group">
                          <label className="form-label">Date admission *</label>
                          <input
                            type="datetime-local"
                            className="form-input"
                            value={hospitalizationForm.admissionDate}
                            onChange={e => setHospitalizationForm(f => ({ ...f, admissionDate: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Date congé</label>
                          <input
                            type="datetime-local"
                            className="form-input"
                            value={hospitalizationForm.dischargeDate}
                            onChange={e => setHospitalizationForm(f => ({ ...f, dischargeDate: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Raison</label>
                          <textarea
                            className="form-textarea"
                            rows={2}
                            value={hospitalizationForm.reason}
                            onChange={e => setHospitalizationForm(f => ({ ...f, reason: e.target.value }))}
                          />
                        </div>
                        <button className="btn btn-primary btn-sm" type="submit" disabled={savingHospitalization}>
                          {savingHospitalization ? 'Enregistrement...' : 'Ajouter'}
                        </button>
                      </form>

                      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 12 }}>
                        <h5 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Suivi de soins</h5>
                        <form onSubmit={handleCreateCareFollowup}>
                          <div className="form-group">
                            <label className="form-label">Date échéance *</label>
                            <input
                              type="datetime-local"
                              className="form-input"
                              value={followupForm.dueDate}
                              onChange={e => setFollowupForm(f => ({ ...f, dueDate: e.target.value }))}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Notes</label>
                            <textarea
                              className="form-textarea"
                              rows={2}
                              value={followupForm.notes}
                              onChange={e => setFollowupForm(f => ({ ...f, notes: e.target.value }))}
                            />
                          </div>
                          <button className="btn btn-primary btn-sm" type="submit" disabled={savingFollowup}>
                            {savingFollowup ? 'Enregistrement...' : 'Créer suivi'}
                          </button>
                        </form>

                        <hr style={{ margin: '12px 0' }} />
                        <h6 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Suivis ouverts</h6>
                        {loadingFollowups ? (
                          <div style={{ padding: 8, textAlign: 'center' }}>
                            <div className="spinner" style={{ margin: 'auto' }} />
                          </div>
                        ) : careFollowups.length === 0 ? (
                          <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Aucun suivi ouvert.</div>
                        ) : (
                          <div style={{ display: 'grid', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
                            {careFollowups.map((f) => (
                              <div
                                key={f.id}
                                style={{
                                  border: '1px solid var(--gray-200)',
                                  borderRadius: 8,
                                  padding: 8,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 10,
                                }}
                              >
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                                    Échéance: {formatDateTime(f.due_date)}
                                  </div>
                                  <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {f.notes || 'Sans note'}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm"
                                  onClick={() => handleCompleteCareFollowup(f.id)}
                                  disabled={completingFollowupId === f.id}
                                >
                                  {completingFollowupId === f.id ? 'Traitement...' : 'Compléter'}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row" style={{ marginTop: 8 }}>
                  <div className="form-group">
                    <label className="form-label">Début période</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={kpiStartDate}
                      onChange={e => setKpiStartDate(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fin période</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={kpiEndDate}
                      onChange={e => setKpiEndDate(e.target.value)}
                    />
                  </div>
                </div>

                {kpiLoading ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: 'auto' }} />
                  </div>
                ) : !patientKpi ? (
                  <div style={{ padding: 12, color: 'var(--gray-500)' }}>
                    Aucune donnée KPI pour cette période.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ width: '100%', height: 260, border: '1px solid var(--gray-200)', borderRadius: 10, padding: 8 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={patientKpiChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'KPI']} />
                          <Bar dataKey="value" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Taux de socialisation</div>
                        <strong>{Number(patientKpi.socialization_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Progression de l'autonomie</div>
                        <strong>{Number(patientKpi.autonomy_progress_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Satisfaction proche aidant</div>
                        <strong>{Number(patientKpi.family_caregiver_satisfaction_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Nombre de chutes</div>
                        <strong>{patientKpi.falls_count || 0}</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Participation aux activités</div>
                        <strong>{Number(patientKpi.user_activity_participation_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Fréquence des activités</div>
                        <strong>{Number(patientKpi.user_activity_frequency || 0).toFixed(1)}</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Réduction hospitalisation</div>
                        <strong>{Number(patientKpi.hospitalization_reduction_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Suivi des soins</div>
                        <strong>{Number(patientKpi.care_followup_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Satisfaction repas</div>
                        <strong>{Number(patientKpi.meal_satisfaction_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                      <div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Taux de présence</div>
                        <strong>{Number(patientKpi.presence_rate_percent || 0).toFixed(1)}%</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
    </AccessibleModal>
  );
};

const PatientCreateModal = ({ centers, onClose, onSave, managerCenterIds }) => {
  const allowedCenterIds = Array.isArray(managerCenterIds) ? managerCenterIds : [];
  const selectableCenters = allowedCenterIds.length
    ? centers.filter(c => allowedCenterIds.includes(c.id))
    : centers;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetNumber: '',
    streetName: '',
    apartment: '',
    city: '',
    province: '',
    postalCode: '',
    languagePref: 'fr',
    centerId: allowedCenterIds[0] || '',
    centerIds: allowedCenterIds.length ? [...allowedCenterIds] : [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await usersAPI.create({
        ...form,
        role: 'usager',
        jobFunction: 'Usager',
        centerId: form.centerId || null,
        centerIds: form.centerIds || [],
      });
      onSave(response?.data || null);
    } catch (err) {
      const firstValidationError = err.response?.data?.errors?.[0]?.msg;
      setError(firstValidationError || err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessibleModal
      title="Ajouter un usager"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button type="submit" form="patient-create-form" className="btn btn-primary" disabled={loading}>
            {loading ? 'Enregistrement...' : 'Créer l’usager'}
          </button>
        </>
      )}
    >
        <form id="patient-create-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Prénom *</label>
                <input
                  className="form-input"
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input
                  className="form-input"
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Courriel *</label>
              <input
                type="email"
                className="form-input"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Téléphone</label>
                <input
                  className="form-input"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">No. rue</label>
                <input
                  className="form-input"
                  value={form.streetNumber}
                  onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nom de la rue</label>
                <input
                  className="form-input"
                  value={form.streetName}
                  onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Appartement</label>
                <input
                  className="form-input"
                  value={form.apartment}
                  onChange={e => setForm(f => ({ ...f, apartment: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Ville</label>
                <input
                  className="form-input"
                  value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Province</label>
                <select
                  className="form-select"
                  value={form.province}
                  onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                >
                  <option value="">— Sélectionner —</option>
                  {CANADIAN_PROVINCES_AND_TERRITORIES.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Code postal</label>
                <input
                  className="form-input"
                  value={form.postalCode}
                  onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Langue</label>
                <select
                  className="form-select"
                  value={form.languagePref}
                  onChange={e => setForm(f => ({ ...f, languagePref: e.target.value }))}
                >
                  <option value="fr">Français</option>
                  <option value="en">Anglais</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Centre</label>
              <select
                className="form-select"
                value={form.centerId}
                onChange={e => setForm(f => ({ ...f, centerId: e.target.value }))}
                disabled={false}
              >
                <option value="">Tous</option>
                {selectableCenters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Centres assignés (multi-sélection)</label>
              <select
                className="form-select"
                multiple
                value={form.centerIds}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setForm(f => ({ ...f, centerIds: selected }));
                }}
                disabled={false}
                style={{ minHeight: 120 }}
              >
                {selectableCenters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="alert alert-warning" role="status">📧 Un mot de passe temporaire sera envoyé par courriel.</div>
        </form>
    </AccessibleModal>
  );
};

// ===== PAGE PRINCIPALE PATIENTS =====
const PatientsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canManagePatients = user?.role === 'admin' || user?.role === 'manager';
  const canEditPatientRecord = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'staff';
  const canDeletePatient = user?.role === 'admin';
  const [patients, setPatients] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [intakeReadOnly, setIntakeReadOnly] = useState(false);
  const [openMenuPatientId, setOpenMenuPatientId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [regeneratingPinPatientId, setRegeneratingPinPatientId] = useState(null);
  const [generatedPinNotice, setGeneratedPinNotice] = useState(null);

  const openPatientIntake = async (patientId, readOnly) => {
    try {
      const res = await patientsAPI.getById(patientId);
      setSelectedPatient(res.data || null);
      setIntakeReadOnly(Boolean(readOnly));
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Impossible de charger le dossier usager');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const jobs = [patientsAPI.getAll({ search })];
      if (canManagePatients) {
        jobs.push(centersAPI.getAll());
      }
      const [patientsRes, centersRes] = await Promise.all(jobs);
      setPatients(patientsRes.data || []);
      if (centersRes) {
        setCenters(centersRes.data || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, canManagePatients]);
  useEffect(() => {
    const closeMenu = () => setOpenMenuPatientId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);
  useEffect(() => {
    const targetPatientId = location.state?.patientId;
    if (!targetPatientId || loading) return;
    const targetPatient = patients.find((p) => p.id === targetPatientId);
    if (!targetPatient) return;
    openPatientIntake(targetPatientId, Boolean(location.state?.openReadOnly));
    navigate('/patients', { replace: true, state: null });
  }, [location.state, loading, patients, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    await usersAPI.delete(id);
    setOpenMenuPatientId(null);
    load();
  };

  const handleRegeneratePin = async (patient) => {
    const confirmMsg = `Regenerer le PIN tablette de ${patient.first_name} ${patient.last_name} et lui envoyer par courriel ?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setRegeneratingPinPatientId(patient.id);
      const res = await usersAPI.regenerateTimeClockPin(patient.id);
      setGeneratedPinNotice({
        pin: res.data?.generatedTimeClockPin,
        name: `${patient.first_name} ${patient.last_name}`,
        autoGenerated: true,
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la regeneration du PIN');
    } finally {
      setRegeneratingPinPatientId(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('patients.title')}</h2>
          <p className="text-muted">{patients.length} usagers</p>
        </div>
      </div>

      {generatedPinNotice ? (
        <div className="alert alert-success" role="status" style={{ marginBottom: 16 }}>
          {generatedPinNotice.autoGenerated ? 'PIN tablette genere' : 'PIN tablette enregistre'} pour {generatedPinNotice.name} : <strong style={{ letterSpacing: '0.08em' }}>{generatedPinNotice.pin}</strong>
        </div>
      ) : null}

      {canManagePatients && (
        <div className="toolbar">
          <div className="search-input-wrapper">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input className="form-input search-input" placeholder="Rechercher un usager..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Ajouter un usager
          </button>
        </div>
      )}

      <div className="card" style={{ overflow: 'visible' }}>
        <div
          className="table-wrapper"
          style={{
            overflowY: 'visible',
            paddingBottom: openMenuPatientId ? 120 : 0,
            transition: 'padding-bottom 0.15s ease',
          }}
        >
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('staff.firstName')} / {t('staff.lastName')}</th>
                  <th>Médecin traitant</th>
                  <th>{t('patients.admissionDate')}</th>
                  <th>Ville</th>
                  <th>PIN tablette</th>
                  <th>Courriel</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {patients.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('patients.noPatients')}</td></tr>
                )}
                {patients.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.first_name} {p.last_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{p.email}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{p.doctor_name || '—'}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{p.admission_date?.split('T')[0] || '—'}</td>
                    <td style={{ fontSize: 13 }}>{p.city || '—'}</td>
                    <td>{p.has_timeclock_pin ? <span className="badge badge-success">Configure</span> : <span className="badge badge-gray">Absent</span>}</td>
                    <td style={{ fontSize: 13 }}>{p.email || '—'}</td>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuPatientId(curr => (curr === p.id ? null : p.id));
                          }}
                          aria-label="Ouvrir le menu d'actions"
                          aria-haspopup="menu"
                          aria-expanded={openMenuPatientId === p.id}
                        >
                          ⋮
                        </button>
                        {openMenuPatientId === p.id && (
                          <div
                            role="menu"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: '110%',
                              bottom: 'auto',
                              right: 0,
                              minWidth: 170,
                              background: 'white',
                              border: '1px solid var(--gray-200)',
                              borderRadius: 8,
                              boxShadow: 'var(--shadow-md)',
                              zIndex: 2000,
                              padding: 6,
                            }}
                          >
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: canEditPatientRecord || canDeletePatient ? 6 : 0 }}
                              onClick={async () => {
                                await openPatientIntake(p.id, true);
                                setOpenMenuPatientId(null);
                              }}
                            >
                              Consulter
                            </button>
                            {canEditPatientRecord && (
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: canDeletePatient ? 6 : 0 }}
                                onClick={async () => {
                                  await openPatientIntake(p.id, false);
                                  setOpenMenuPatientId(null);
                                }}
                              >
                                Modifier
                              </button>
                            )}
                            {canManagePatients && (
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: canDeletePatient ? 6 : 0 }}
                                onClick={() => {
                                  setOpenMenuPatientId(null);
                                  handleRegeneratePin(p);
                                }}
                                disabled={regeneratingPinPatientId === p.id}
                              >
                                {regeneratingPinPatientId === p.id ? 'Generation...' : 'Regenerer le PIN'}
                              </button>
                            )}
                            {canDeletePatient && (
                              <button
                                className="btn btn-sm btn-danger"
                                style={{ width: '100%', justifyContent: 'flex-start' }}
                                onClick={() => handleDelete(p.id)}
                              >
                                {t('common.delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedPatient && (
        <PatientIntakeModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onSave={() => { setSelectedPatient(null); load(); }}
          t={t}
          isReadOnly={intakeReadOnly || user?.role === 'usager'}
        />
      )}

      {showCreateModal && (
        <PatientCreateModal
          centers={centers}
          managerCenterIds={
            user?.role === 'manager'
              ? (Array.isArray(user.centerIds) && user.centerIds.length
                ? user.centerIds
                : (user.centerId ? [user.centerId] : []))
              : []
          }
          onClose={() => setShowCreateModal(false)}
          onSave={(result) => {
            setShowCreateModal(false);
            if (result?.generatedTimeClockPin && result?.user) {
              setGeneratedPinNotice({
                pin: result.generatedTimeClockPin,
                name: `${result.user.first_name} ${result.user.last_name}`,
                autoGenerated: !!result.autoGeneratedTimeClockPin,
              });
            } else {
              setGeneratedPinNotice(null);
            }
            load();
          }}
        />
      )}
    </div>
  );
};

export default PatientsPage;
